import { datasetRowIdColumn } from '@cotera/sdk/core';
import type { DatasetOperationV20260601 } from '@cotera/api';
import {
  ParquetBackedTable,
  duckDbIdentifier,
} from '@cotera/client/app/etc/duckdb';
import type { ParquetBackedTableDb } from '@cotera/client/app/etc/duckdb';
import { DuckDbLayerStack } from '@cotera/client/app/etc/duckdb/layers';
import { datasetPreviewDuckDb } from '@cotera/client/app/etc/duckdb/preview-db';
import {
  DATASET_PREVIEW_ORDER_COLUMN,
  type DatasetPreviewColumn,
} from './operations';
import {
  createDatasetOperationsLayer,
  type DatasetOperationSkipReporter,
} from './operations-layer';
import { createPendingEditsLayer, DatasetPendingEdits } from './pending-edits';
import type { DatasetPreviewRow } from './types';

// Row identity, the internal-column predicate and the row-id resolver live in
// the SDK so the grid, the operations service and the compaction saga cannot
// drift apart. This file previously carried its own copies, and its internal
// column set had already fallen two entries behind the server's.

/**
 * The DuckDB table one dataset artifact is previewed through.
 *
 * A `ParquetBackedTable` that knows what the parquet is called and what gets
 * replayed onto it. Everything generic — registering the file, rebuilding under
 * one lock, running the stack — lives in the primitive; this only supplies the
 * dataset's half.
 *
 * Not a `BaseModel`: nothing registers it in a model scope or looks it up by
 * id, and it has no event subscriptions to dispose. It is infrastructure, like
 * `CoteraDuckDB`.
 */
export class DatasetPreviewTable {
  /** Raw table name. */
  readonly name: string;
  /** The quoted identifier, for interpolating into a query. */
  readonly sql: string;

  private readonly table: ParquetBackedTable;
  private readonly onOperationsSkipped?: DatasetOperationSkipReporter;

  /**
   * Named for the *dataset*, not the artifact it currently holds.
   *
   * A dataset gets a new artifact whenever compaction folds pending edits in —
   * which, on a dataset a workflow writes back to, is often. Naming the table
   * after the artifact made every one of those a different table, and the table
   * name reaches all the way out to the grid's adapter: a new name meant a new
   * adapter, and a new adapter rebuilds the whole view model, resetting sorts,
   * filters, selection, expansion and scroll. The rows were usually identical.
   *
   * The dataset is the thing whose rows the grid is showing, so it is the right
   * identity for the table holding them. Which artifact filled it is a property
   * of the contents, not of the table, and lives in {@link load}'s argument.
   */
  constructor(
    datasetId: string,
    db: Promise<ParquetBackedTableDb> = datasetPreviewDuckDb,
    onOperationsSkipped?: DatasetOperationSkipReporter
  ) {
    this.name = `dataset_preview_${datasetId.replaceAll('-', '_')}`;
    this.table = new ParquetBackedTable(this.name, db);
    this.sql = this.table.sql;
    this.onOperationsSkipped = onOperationsSkipped;
  }

  /**
   * Name an artifact's parquet is registered under in the wasm filesystem.
   *
   * Still per-artifact, unlike the table: `registerFileURL` caches by name, so a
   * name has to identify its bytes or a later artifact would be served the
   * earlier one's.
   */
  private fileNameFor(artifactId: string): string {
    return `dataset-${artifactId}.parquet`;
  }

  /**
   * Points DuckDB at an artifact's parquet over HTTP. Idempotent.
   *
   * `replace` re-points the name at a freshly resolved URL, for a retry after
   * the previous one stopped answering — an expired signature, or an artifact
   * pruned out from under the grid while its dataset is being written.
   */
  async register(
    artifactId: string,
    url: string,
    options: { replace?: boolean } = {}
  ): Promise<void> {
    await this.table.register(this.fileNameFor(artifactId), url, options);
  }

  /**
   * Rebuilds from the parquet, then replays what the server knows and what the
   * user has changed since. Returns the schema the table ends up with, or
   * `undefined` when a later run superseded this one.
   *
   * The replay is generated rather than issued here: the layers turn the log
   * and the pending edits into statements. What an operation *means* lives in
   * `operations.ts`, next to what it does to the schema — and the schema this
   * returns comes out of that same fold, so a column the replay adds cannot be
   * missing from the grid and one it drops cannot linger.
   */
  async load(
    input: {
      /** The artifact whose parquet fills the table on this run. */
      artifactId: string;
      operations: readonly DatasetOperationV20260601[];
      /**
       * The artifact's schema, *before* the replay. The replay resolves the
       * row-id column per step from this, so it has to start where the parquet
       * does rather than where the grid ends up.
       */
      columns: readonly DatasetPreviewColumn[];
      pendingEdits: DatasetPendingEdits;
    },
    options: { shouldRun?: () => boolean } = {}
  ): Promise<readonly DatasetPreviewColumn[] | undefined> {
    return await this.table.load<DatasetPreviewRow>(
      {
        fileName: this.fileNameFor(input.artifactId),
        // The order column rides along on the base so an inserted row can be
        // positioned fractionally between its neighbours.
        select: `, CAST(row_number() OVER () AS DOUBLE) AS ${duckDbIdentifier(
          DATASET_PREVIEW_ORDER_COLUMN
        )}`,
        // In the order it has to run: what the server has, then what the user
        // has done since. Composing rather than calling the two in sequence is
        // what threads the schema — and with it the row-id column — from one
        // into the next.
        layers: new DuckDbLayerStack<DatasetPreviewRow>([
          createDatasetOperationsLayer(
            input.operations,
            this.onOperationsSkipped
          ),
          createPendingEditsLayer(input.pendingEdits),
        ]),
        baseColumns: input.columns,
        resolveRowIdColumn: datasetRowIdColumn,
      },
      options
    );
  }

  /**
   * Replays operations onto the table as it already stands.
   *
   * The same statements {@link load} would have generated for them, run against
   * the table that is already there instead of a fresh one — so a write that
   * arrived from a workflow, from Coco or from another tab shows up without the
   * parquet being re-read and the grid rebuilt around it.
   *
   * `columns` is the schema the table currently has, not the parquet's: the fold
   * resolves the row-id column from what it is handed, and here that is the
   * post-replay schema rather than the pre-replay one.
   *
   * The pending-edits layer is deliberately absent. It exists to put unsaved
   * edits back after a rebuild wiped them, and nothing here wipes anything —
   * replaying it would write stale local values over cells this batch just
   * changed.
   */
  async apply(input: {
    operations: readonly DatasetOperationV20260601[];
    columns: readonly DatasetPreviewColumn[];
  }): Promise<void> {
    if (input.operations.length === 0) {
      return;
    }
    await this.table.apply<DatasetPreviewRow>({
      layers: new DuckDbLayerStack<DatasetPreviewRow>([
        createDatasetOperationsLayer(
          input.operations,
          this.onOperationsSkipped
        ),
      ]),
      baseColumns: input.columns,
      resolveRowIdColumn: datasetRowIdColumn,
    });
  }
}
